import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { visitOptimizerApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Compass, MapPin, Clock, Navigation, Loader2, Route, Car,
  Bike, Train, Bus, Footprints, User, Zap,
  Calendar, AlertTriangle, TrendingUp, Eye, History,
  Lock, ArrowUpRight, Crown, Sparkles, Shield
} from 'lucide-react';

const TRANSPORT_ICONS = {
  walking: Footprints, 'auto-rickshaw': Car, auto: Car, cab: Car, taxi: Car,
  car: Car, bus: Bus, train: Train, bike: Bike, 'two-wheeler': Bike,
};

function getTransportIcon(mode) {
  if (!mode) return Car;
  const lower = mode.toLowerCase();
  for (const [key, Icon] of Object.entries(TRANSPORT_ICONS)) {
    if (lower.includes(key)) return Icon;
  }
  return Car;
}

const PRIORITY_COLORS = {
  high: 'bg-red-500/10 text-red-400 border-red-500/30',
  medium: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  low: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
};

/* ─── UPGRADE PROMPT CARDS ──────────────────────────────── */

function UpgradeCard({ from, to, features, ctaText, highlight }) {
  const navigate = useNavigate();
  const tierColors = {
    professional: { bg: 'from-[#002FA7]/20 to-[#0047FF]/10', border: 'border-[#002FA7]/40', badge: 'bg-[#002FA7]/20 text-blue-300', icon: Crown },
    enterprise: { bg: 'from-amber-500/15 to-orange-500/10', border: 'border-amber-500/40', badge: 'bg-amber-500/20 text-amber-300', icon: Sparkles },
  };
  const colors = tierColors[to] || tierColors.enterprise;
  const TierIcon = colors.icon;

  return (
    <div className={`relative overflow-hidden bg-gradient-to-br ${colors.bg} ${colors.border} border rounded-xl p-5`} data-testid={`upgrade-card-${to}`}>
      {highlight && (
        <div className="absolute top-0 right-0 bg-amber-500 text-black text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
          Most Popular
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        <TierIcon className="w-5 h-5 text-amber-400" />
        <span className="text-white font-semibold text-base">Upgrade to {to === 'professional' ? 'Professional' : 'Enterprise'}</span>
      </div>
      <p className="text-gray-400 text-sm mb-4 leading-relaxed">
        {to === 'professional'
          ? "Unlock road distances, GPS live tracking, and smart task prioritization to supercharge your field team's efficiency."
          : "Get the full AI-powered Visit Optimizer — intelligent day planning, transport mode suggestions with costs, and team-wide admin oversight."}
      </p>
      <ul className="space-y-2 mb-5">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Zap className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <span className="text-gray-300">{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={() => navigate('/subscription')}
        className={`w-full ${to === 'enterprise' ? 'bg-amber-500 hover:bg-amber-600 text-black' : 'bg-[#002FA7] hover:bg-[#001d6e] text-white'} font-semibold gap-2`}
        data-testid={`upgrade-btn-${to}`}
      >
        {ctaText || `Upgrade to ${to === 'professional' ? 'Professional' : 'Enterprise'}`}
        <ArrowUpRight className="w-4 h-4" />
      </Button>
    </div>
  );
}

function BlurredPreview({ label }) {
  return (
    <div className="relative rounded-xl overflow-hidden" data-testid="blurred-preview">
      <div className="blur-sm pointer-events-none select-none opacity-50">
        <div className="bg-gradient-to-r from-[#002FA7]/20 to-[#0047FF]/10 border border-[#002FA7]/30 rounded-lg p-4 mb-3">
          <div className="grid grid-cols-4 gap-3">
            {['Total Visits', 'Distance', 'Travel Time', 'Efficiency'].map(l => (
              <div key={l}>
                <div className="text-gray-500 text-xs">{l}</div>
                <div className="text-white font-bold text-lg mt-1">--</div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3 p-3 bg-[#0D1117] border border-gray-800 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-[#002FA7]/30 flex items-center justify-center text-white font-bold">{i}</div>
              <div className="flex-1">
                <div className="h-4 bg-gray-700 rounded w-48 mb-2" />
                <div className="h-3 bg-gray-800 rounded w-32" />
                <div className="flex gap-3 mt-2">
                  <div className="h-3 bg-gray-800 rounded w-20" />
                  <div className="h-3 bg-gray-800 rounded w-16" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[2px] rounded-xl">
        <div className="text-center px-4">
          <Lock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
          <p className="text-white font-semibold text-sm">{label}</p>
          <p className="text-gray-400 text-xs mt-1">This feature is locked on your current plan</p>
        </div>
      </div>
    </div>
  );
}

/* ─── OPTIMIZATION RESULT ──────────────────────────────── */

function OptimizationResultCard({ plan }) {
  if (!plan || !plan.optimized_plan) return null;
  const summary = plan.day_summary || {};
  const transport = plan.transport_overview || {};

  return (
    <div className="space-y-4" data-testid="optimization-result">
      <div className="bg-gradient-to-r from-[#002FA7]/20 to-[#0047FF]/10 border border-[#002FA7]/30 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-5 h-5 text-[#3B82F6]" />
          <span className="text-sm font-semibold text-white">AI Optimization Summary</span>
          {summary.efficiency_score && (
            <Badge className="ml-auto bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
              Score: {summary.efficiency_score}/100
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          {[
            ['Total Visits', summary.total_visits || plan.optimized_plan.length],
            ['Distance', `${summary.total_distance_km || '--'} km`],
            ['Travel Time', summary.total_travel_time || '--'],
            ['Productive Time', summary.total_productive_time || summary.productive_time || '--'],
          ].map(([label, val]) => (
            <div key={label}>
              <div className="text-gray-400">{label}</div>
              <div className="text-white font-bold text-lg">{val}</div>
            </div>
          ))}
        </div>
      </div>

      {transport.primary_mode && (
        <div className="bg-[#0D1117] border border-gray-800 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Car className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-gray-300">Transport Overview</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-400">
            <div><span className="text-gray-500">Mode:</span> {transport.primary_mode}</div>
            <div><span className="text-gray-500">Est. Cost:</span> {transport.estimated_transport_cost || '--'}</div>
            {transport.fuel_saving_tip && (
              <div className="col-span-1 sm:col-span-3 text-emerald-400 text-xs mt-1">
                <TrendingUp className="w-3 h-3 inline mr-1" />{transport.fuel_saving_tip}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="space-y-1">
        <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <Route className="w-4 h-4 text-[#3B82F6]" /> Optimized Visit Sequence
        </h4>
        {plan.optimized_plan.map((visit, idx) => {
          const TransportIcon = getTransportIcon(visit.transport_mode);
          return (
            <div key={idx} className="relative" data-testid={`visit-step-${idx}`}>
              {idx < plan.optimized_plan.length - 1 && (
                <div className="absolute left-[19px] top-[48px] bottom-[-4px] w-[2px] bg-gray-700" />
              )}
              <div className="flex gap-3 p-3 bg-[#0D1117] border border-gray-800 rounded-lg hover:border-[#002FA7]/50 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#002FA7]/30 border border-[#002FA7]/50 flex items-center justify-center text-white font-bold text-sm">
                  {visit.order || idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium text-sm">{visit.customer_name}</span>
                    <Badge className={`text-xs ${PRIORITY_COLORS[visit.priority] || PRIORITY_COLORS.medium}`}>{visit.priority}</Badge>
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5 truncate">{visit.task_title}</div>
                  <div className="flex items-center gap-4 mt-2 flex-wrap">
                    {visit.transport_mode && (
                      <div className="flex items-center gap-1 text-xs text-blue-400"><TransportIcon className="w-3 h-3" />{visit.transport_mode}</div>
                    )}
                    {visit.estimated_travel_time && (
                      <div className="flex items-center gap-1 text-xs text-gray-400"><Clock className="w-3 h-3" />{visit.estimated_travel_time}</div>
                    )}
                    {visit.distance_from_previous_km > 0 && (
                      <div className="flex items-center gap-1 text-xs text-gray-400"><Navigation className="w-3 h-3" />{visit.distance_from_previous_km} km</div>
                    )}
                    {visit.suggested_arrival && (
                      <div className="flex items-center gap-1 text-xs text-emerald-400"><Calendar className="w-3 h-3" />Arrive: {visit.suggested_arrival}</div>
                    )}
                  </div>
                  {visit.transport_details && (
                    <div className="text-xs text-gray-500 mt-1.5 leading-relaxed">{visit.transport_details}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {plan.pro_tips && plan.pro_tips.length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
          <div className="text-sm font-medium text-amber-300 mb-2 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> Pro Tips
          </div>
          <ul className="space-y-1">
            {plan.pro_tips.map((tip, i) => (
              <li key={i} className="text-xs text-gray-400 flex items-start gap-2">
                <span className="text-amber-400 mt-0.5">-</span><span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {plan.skipped_tasks && plan.skipped_tasks.length > 0 && (
        <div className="bg-gray-500/5 border border-gray-700 rounded-lg p-3">
          <div className="text-sm font-medium text-gray-400 mb-2">Skipped (Recommended for later)</div>
          {plan.skipped_tasks.map((s, i) => (
            <div key={i} className="text-xs text-gray-500 mb-1">
              <span className="text-gray-300">{s.task_id?.slice(0, 8)}...</span> — {s.reason}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── NEARBY TASKS (FULL) ──────────────────────────────── */

function NearbyTasksFull({ tasks, loading: loadingProp }) {
  if (loadingProp) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-[#3B82F6]" />
      <span className="ml-2 text-gray-400 text-sm">Scanning nearby tasks...</span>
    </div>
  );
  if (!tasks || tasks.length === 0) return (
    <div className="text-center py-12 text-gray-500">
      <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
      <p className="text-sm">No nearby tasks found within radius.</p>
    </div>
  );

  return (
    <div className="space-y-2" data-testid="nearby-tasks-panel">
      {tasks.map((task, idx) => (
        <div key={task.id || idx} className="flex items-center gap-3 p-3 bg-[#0D1117] border border-gray-800 rounded-lg hover:border-[#002FA7]/40 transition-colors" data-testid={`nearby-task-${idx}`}>
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#002FA7]/20 flex items-center justify-center text-xs font-bold text-blue-400">
            {task.distance_km}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-white text-sm font-medium truncate">{task.customer_name}</span>
              <Badge className={`text-xs ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`}>{task.priority}</Badge>
            </div>
            <div className="text-xs text-gray-400 truncate">{task.title}</div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              <span className="flex items-center gap-1"><Navigation className="w-3 h-3" />{task.distance_km} km</span>
              {task.road_distance_km && <span className="flex items-center gap-1"><Car className="w-3 h-3" />{task.road_distance_km} km road</span>}
              {task.road_duration_mins && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />~{task.road_duration_mins} min</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── NEARBY TASKS (LITE — Starter) ──────────────────── */

function NearbyTasksLite({ tasks, hiddenCount, loading: loadingProp }) {
  if (loadingProp) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-[#3B82F6]" />
      <span className="ml-2 text-gray-400 text-sm">Scanning nearby tasks...</span>
    </div>
  );
  if (!tasks || tasks.length === 0) return (
    <div className="text-center py-12 text-gray-500">
      <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
      <p className="text-sm">No nearby tasks found within radius.</p>
    </div>
  );

  return (
    <div className="space-y-2" data-testid="nearby-tasks-lite">
      {tasks.map((task, idx) => (
        <div key={task.id || idx} className="flex items-center gap-3 p-3 bg-[#0D1117] border border-gray-800 rounded-lg" data-testid={`nearby-task-lite-${idx}`}>
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-700/50 flex items-center justify-center text-xs font-bold text-gray-300">
            {task.distance_km}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-white text-sm font-medium truncate">{task.customer_name}</span>
              <Badge className={`text-xs ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`}>{task.priority}</Badge>
            </div>
            <div className="text-xs text-gray-400 truncate">{task.title}</div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              <span className="flex items-center gap-1"><Navigation className="w-3 h-3" />~{task.distance_km} km away</span>
              <span className="flex items-center gap-1 text-gray-600"><Lock className="w-3 h-3" />Road distance locked</span>
            </div>
          </div>
        </div>
      ))}
      {hiddenCount > 0 && (
        <div className="text-center py-3 bg-[#0D1117]/50 border border-dashed border-gray-700 rounded-lg">
          <Lock className="w-4 h-4 mx-auto text-gray-500 mb-1" />
          <p className="text-xs text-gray-500">+{hiddenCount} more task{hiddenCount > 1 ? 's' : ''} hidden</p>
          <p className="text-xs text-amber-400 mt-0.5">Upgrade to Professional to see all</p>
        </div>
      )}
    </div>
  );
}

/* ─── ADMIN OVERVIEW ──────────────────────────────── */

function AdminOverviewPanel() {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSP, setSelectedSP] = useState(null);
  const [spPlan, setSpPlan] = useState(null);
  const [spPlanLoading, setSpPlanLoading] = useState(false);

  useEffect(() => { fetchOverview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOverview = async () => {
    try {
      const res = await visitOptimizerApi.adminOverview();
      setOverview(res.data.salespersons || []);
    } catch { toast.error('Failed to load overview'); }
    finally { setLoading(false); }
  };

  const viewSPPlan = async (sp) => {
    setSelectedSP(sp);
    setSpPlanLoading(true);
    try {
      const res = await visitOptimizerApi.getHistory({ user_id: sp.user_id, limit: 1 });
      setSpPlan((res.data.plans || [])[0] || null);
    } catch { toast.error('Failed to load plan'); }
    finally { setSpPlanLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#3B82F6]" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-overview-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {overview.map((sp) => (
          <div key={sp.user_id}
            className={`p-3 bg-[#0D1117] border rounded-lg cursor-pointer transition-all ${selectedSP?.user_id === sp.user_id ? 'border-[#002FA7] ring-1 ring-[#002FA7]/50' : 'border-gray-800 hover:border-gray-600'}`}
            onClick={() => viewSPPlan(sp)} data-testid={`sp-card-${sp.user_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2 h-2 rounded-full ${sp.status === 'on_beat' ? 'bg-emerald-400 animate-pulse' : sp.status === 'online' ? 'bg-blue-400' : 'bg-gray-600'}`} />
              <span className="text-white text-sm font-medium">{sp.name}</span>
              {sp.has_active_beat && <Badge className="bg-emerald-500/20 text-emerald-300 text-xs border-emerald-500/40">On Beat</Badge>}
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{sp.nearby_task_count} nearby</span>
              {sp.latest_optimization && <span className="flex items-center gap-1"><Eye className="w-3 h-3" />Has plan</span>}
            </div>
          </div>
        ))}
        {overview.length === 0 && <div className="col-span-3 text-center py-8 text-gray-500 text-sm">No salespersons with known locations found.</div>}
      </div>
      {selectedSP && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
            <User className="w-4 h-4 text-[#3B82F6]" /> {selectedSP.name}'s Latest Optimization Plan
          </h3>
          {spPlanLoading ? <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#3B82F6]" /></div>
           : spPlan ? <OptimizationResultCard plan={spPlan.ai_plan} />
           : <div className="text-center py-8 text-gray-500 text-sm">No optimization plan generated yet for {selectedSP.name}.</div>}
        </div>
      )}
    </div>
  );
}

/* ─── MAIN PAGE ──────────────────────────────── */

export default function VisitOptimizerPage() {
  const { user, isOwner } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager' || isOwner;
  const isSalesperson = user?.role === 'salesperson';

  const [accessInfo, setAccessInfo] = useState(null);
  const [nearbyTasks, setNearbyTasks] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [upgradeHint, setUpgradeHint] = useState('');
  const [aiPlan, setAiPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryPlan, setSelectedHistoryPlan] = useState(null);
  const [tierLoading, setTierLoading] = useState(true);

  // Fetch tier info + GPS
  useEffect(() => {
    visitOptimizerApi.tierInfo()
      .then(res => setAccessInfo(res.data))
      .catch(() => setAccessInfo({ access_level: 'starter', can_scan_nearby: true, can_optimize_day: false, can_view_admin_overview: false, can_view_history: false, can_see_road_distance: false }))
      .finally(() => setTierLoading(false));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCurrentLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {
          setLocationError('Location access denied. Please enable GPS.');
          if (user?.current_location) setCurrentLocation({ lat: user.current_location.lat, lng: user.current_location.lng });
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, [user]);

  const checkNearby = useCallback(async () => {
    if (!currentLocation) { toast.error('Location not available.'); return; }
    setLoading(true);
    setAiPlan(null);
    try {
      const res = await visitOptimizerApi.nearbyCheck({
        lat: currentLocation.lat, lng: currentLocation.lng, radius_km: 50, trigger: 'manual',
      });
      setNearbyTasks(res.data.tasks || []);
      setHiddenCount(res.data.tasks_hidden || 0);
      setUpgradeHint(res.data.upgrade_hint || '');
      if (res.data.nearby_count === 0) toast.info('No nearby tasks within 50km.');
      else toast.success(`Found ${res.data.nearby_count} nearby task(s)!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to scan nearby tasks');
    } finally { setLoading(false); }
  }, [currentLocation]);

  const optimizeDay = useCallback(async () => {
    if (!currentLocation) { toast.error('Location not available.'); return; }
    setOptimizing(true);
    try {
      const res = await visitOptimizerApi.optimizeDay({
        lat: currentLocation.lat, lng: currentLocation.lng, radius_km: 50, max_visits: 10,
      });
      setNearbyTasks(res.data.nearby_tasks || []);
      setAiPlan(res.data.ai_plan || null);
      if (res.data.nearby_count === 0) toast.info('No tasks to optimize.');
      else toast.success('Day plan optimized by AI!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Optimization failed');
    } finally { setOptimizing(false); }
  }, [currentLocation]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await visitOptimizerApi.getHistory({ limit: 10 });
      setHistory(res.data.plans || []);
    } catch {
      // Will fail for non-enterprise — expected
      setHistory([]);
    } finally { setHistoryLoading(false); }
  }, []);

  if (tierLoading) return (
    <MainLayout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    </MainLayout>
  );

  const level = accessInfo?.access_level || 'starter';
  const role = accessInfo?.role || user?.role || 'salesperson';
  const canOptimize = accessInfo?.can_optimize_day || false;
  const canSeeRoad = accessInfo?.can_see_road_distance || false;
  const canHistory = accessInfo?.can_view_history || false;
  const canTeamOverview = accessInfo?.can_view_team_overview || false;

  const defaultTab = canTeamOverview && (role === 'admin' || role === 'manager' || isOwner) ? 'overview' : 'scan';

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="visit-optimizer-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Compass className="w-6 h-6 text-[#3B82F6]" />
              Smart Visit Optimizer
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              AI-powered proximity detection, route planning & transport suggestions
            </p>
          </div>
          <div className="flex items-center gap-2">
            {currentLocation && (
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs">
                <MapPin className="w-3 h-3 mr-1" /> GPS Active
              </Badge>
            )}
            <Badge className={`text-xs ${level === 'enterprise' ? 'bg-amber-500/10 text-amber-300 border-amber-500/30' : level === 'professional' ? 'bg-[#002FA7]/20 text-blue-300 border-[#002FA7]/40' : 'bg-gray-700/50 text-gray-300 border-gray-600'}`} data-testid="tier-badge">
              <Shield className="w-3 h-3 mr-1" />
              {level === 'enterprise' ? 'Enterprise' : level === 'professional' ? 'Professional' : 'Starter'}
            </Badge>
          </div>
        </div>

        {locationError && !currentLocation && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
            <AlertTriangle className="w-4 h-4 inline mr-2" />{locationError}
          </div>
        )}

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="bg-[#0D1117] border border-gray-800 mb-4">
            <TabsTrigger value="scan" className="data-[state=active]:bg-[#002FA7]/30 data-[state=active]:text-white" data-testid="tab-scan">
              <MapPin className="w-4 h-4 mr-1" /> Nearby Tasks
            </TabsTrigger>
            {(canOptimize || isSalesperson || isOwner) && (
              <TabsTrigger value="optimize" className="data-[state=active]:bg-[#002FA7]/30 data-[state=active]:text-white" data-testid="tab-optimize">
                <Zap className="w-4 h-4 mr-1" /> Optimize My Day
                {!canOptimize && <Lock className="w-3 h-3 ml-1 opacity-60" />}
              </TabsTrigger>
            )}
            {canTeamOverview && (
              <TabsTrigger value="overview" className="data-[state=active]:bg-[#002FA7]/30 data-[state=active]:text-white" data-testid="tab-overview">
                <Eye className="w-4 h-4 mr-1" /> {role === 'manager' ? 'My Team' : 'Team Overview'}
              </TabsTrigger>
            )}
            <TabsTrigger value="history" className="data-[state=active]:bg-[#002FA7]/30 data-[state=active]:text-white" data-testid="tab-history" onClick={() => canHistory && fetchHistory()}>
              <History className="w-4 h-4 mr-1" /> History
              {!canHistory && isSalesperson && <Lock className="w-3 h-3 ml-1 opacity-60" />}
            </TabsTrigger>
          </TabsList>

          {/* ─── TAB: Nearby Tasks (All Tiers) ─── */}
          <TabsContent value="scan">
            <div className="space-y-4">
              <Button onClick={checkNearby} disabled={loading || !currentLocation}
                className="bg-[#002FA7] hover:bg-[#002FA7]/80 text-white" data-testid="check-nearby-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                Scan Nearby Tasks (50km)
              </Button>

              {(nearbyTasks.length > 0 || loading) && (
                <Card className="bg-[#161B22] border-gray-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-300 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#3B82F6]" />
                      Nearby Tasks ({nearbyTasks.length}{hiddenCount > 0 ? ` + ${hiddenCount} hidden` : ''})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {level === 'starter' && !isOwner
                      ? <NearbyTasksLite tasks={nearbyTasks} hiddenCount={hiddenCount} loading={loading} />
                      : <NearbyTasksFull tasks={nearbyTasks} loading={loading} />
                    }
                  </CardContent>
                </Card>
              )}

              {/* Upgrade prompts based on tier */}
              {level === 'starter' && !isOwner && nearbyTasks.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <UpgradeCard
                    from="starter" to="professional"
                    features={[
                      'Real road distances & travel times (OSRM)',
                      'See ALL nearby tasks (no limit)',
                      'GPS Live Tracking for field teams',
                      'Smart Task Prioritization with AI',
                    ]}
                    ctaText="Unlock Road Distances"
                  />
                  <UpgradeCard
                    from="starter" to="enterprise" highlight
                    features={[
                      'AI-powered full day optimization',
                      'Transport mode & cost suggestions',
                      'Auto-triggers on beat start & check-in',
                      'Admin team-wide oversight panel',
                      'Customer Intelligence & CRM',
                    ]}
                    ctaText="Get Full AI Optimizer"
                  />
                </div>
              )}

              {level === 'professional' && !isOwner && nearbyTasks.length > 0 && (
                <UpgradeCard
                  from="professional" to="enterprise" highlight
                  features={[
                    'AI generates optimized day plan with visit sequence',
                    'Transport mode per stop (auto, bus, train, cab) with estimated costs',
                    'Auto-triggers on beat start, check-in & task assignment',
                    'Admin dashboard with team-wide optimization overview',
                    'Customer Intelligence & CRM Integrations',
                  ]}
                  ctaText="Unlock AI Day Optimization"
                />
              )}

              {!loading && nearbyTasks.length === 0 && (
                <Card className="bg-[#161B22] border-gray-800">
                  <CardContent className="py-12 text-center">
                    <Compass className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                    <h3 className="text-white font-medium mb-1">Ready to Scan</h3>
                    <p className="text-gray-400 text-sm max-w-md mx-auto">
                      Click "Scan Nearby Tasks" to discover tasks within 50km of your current location.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* ─── TAB: Optimize My Day (Enterprise only) ─── */}
          <TabsContent value="optimize">
            {canOptimize ? (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  <Button onClick={checkNearby} disabled={loading || !currentLocation}
                    className="bg-[#0D1117] border border-gray-700 hover:border-[#002FA7] text-white" data-testid="scan-btn-optimize">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                    Scan Nearby
                  </Button>
                  <Button onClick={optimizeDay} disabled={optimizing || !currentLocation}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="optimize-day-btn">
                    {optimizing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                    {optimizing ? 'AI is Optimizing...' : 'Optimize My Day'}
                  </Button>
                </div>

                {(nearbyTasks.length > 0 || loading) && (
                  <Card className="bg-[#161B22] border-gray-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-gray-300 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#3B82F6]" /> Nearby Tasks ({nearbyTasks.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent><NearbyTasksFull tasks={nearbyTasks} loading={loading} /></CardContent>
                  </Card>
                )}

                {aiPlan && (
                  <Card className="bg-[#161B22] border-gray-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-gray-300 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-400" /> AI-Optimized Day Plan
                      </CardTitle>
                    </CardHeader>
                    <CardContent><OptimizationResultCard plan={aiPlan} /></CardContent>
                  </Card>
                )}

                {!loading && !optimizing && nearbyTasks.length === 0 && !aiPlan && (
                  <Card className="bg-[#161B22] border-gray-800">
                    <CardContent className="py-12 text-center">
                      <Sparkles className="w-12 h-12 mx-auto mb-3 text-amber-400/40" />
                      <h3 className="text-white font-medium mb-1">AI Day Optimizer</h3>
                      <p className="text-gray-400 text-sm max-w-md mx-auto">
                        Click "Optimize My Day" and our AI will find nearby tasks, calculate the best route, suggest transport modes, and estimate costs — all in one click.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              /* Locked: Show blurred preview + upgrade card */
              <div className="space-y-5">
                <BlurredPreview label="AI-Powered Day Optimization — Enterprise Only" />
                <UpgradeCard
                  from={level} to="enterprise" highlight
                  features={[
                    'One-click AI generates your entire day plan',
                    'Smart transport suggestions: auto, bus, train, cab with real costs',
                    'Priority-based visit sequencing with travel time buffers',
                    'Auto-triggers when you start a beat or check in',
                    'Estimated transport costs and fuel-saving tips',
                  ]}
                  ctaText="Unlock AI Day Optimization — Enterprise"
                />
              </div>
            )}
          </TabsContent>

          {/* ─── TAB: Team Overview (Admin/Manager — all tiers via RBAC) ─── */}
          <TabsContent value="overview">
            {canTeamOverview ? (
              <Card className="bg-[#161B22] border-gray-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-300 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#3B82F6]" /> {role === 'manager' ? 'My Team Visit Plans' : 'Salesperson Visit Plans'}
                  </CardTitle>
                </CardHeader>
                <CardContent><AdminOverviewPanel /></CardContent>
              </Card>
            ) : (
              <div className="space-y-5">
                <BlurredPreview label="Team-wide Visit Optimization Overview — Enterprise Only" />
                <UpgradeCard
                  from={level} to="enterprise" highlight
                  features={[
                    'See optimization plans for every salesperson',
                    'Monitor nearby task counts per team member',
                    'View AI-generated day plans and efficiency scores',
                    'Track who has active beat sessions',
                    'Full team productivity analytics',
                  ]}
                  ctaText="Unlock Team Overview — Enterprise"
                />
              </div>
            )}
          </TabsContent>

          {/* ─── TAB: History (Enterprise only) ─── */}
          <TabsContent value="history">
            {canHistory ? (
              <Card className="bg-[#161B22] border-gray-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-300 flex items-center gap-2">
                    <History className="w-4 h-4 text-[#3B82F6]" /> Past Optimization Plans
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#3B82F6]" /></div>
                  ) : history.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 text-sm">No history yet. Use "Optimize My Day" to generate your first plan.</div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-2">
                        {history.map((plan, idx) => (
                          <Button key={plan.id} variant={selectedHistoryPlan?.id === plan.id ? 'default' : 'outline'} size="sm"
                            className={selectedHistoryPlan?.id === plan.id ? 'bg-[#002FA7] text-white' : 'border-gray-700 text-gray-300'}
                            onClick={() => setSelectedHistoryPlan(plan)} data-testid={`history-plan-${idx}`}>
                            <Calendar className="w-3 h-3 mr-1" />
                            {new Date(plan.created_at).toLocaleDateString()} — {plan.nearby_count} tasks
                          </Button>
                        ))}
                      </div>
                      {selectedHistoryPlan?.ai_plan && <OptimizationResultCard plan={selectedHistoryPlan.ai_plan} />}
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-5">
                <BlurredPreview label="Optimization History — Enterprise Only" />
                <UpgradeCard
                  from={level} to="enterprise"
                  features={[
                    'View all past AI optimization plans',
                    'Compare efficiency scores across days',
                    'Track transport costs over time',
                    'Analyze visit patterns and productivity trends',
                  ]}
                  ctaText="Unlock History — Enterprise"
                />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
